import { useState, useEffect, useCallback } from 'preact/hooks'
import type { CloudTask, CloudProject } from '../../types'
import { TASK_STATUS_LABELS, TASK_PRIORITY_LABELS, TASK_PRIORITY_COLORS, TASK_TYPE_LABELS, PERSONA_INFO } from '../../types'
import {
  listCloudTasks,
  listCloudProjects,
  listCloudPersonas,
  upsertCloudTask,
  deleteCloudTask,
  upsertCloudProject,
  downloadBlobAsUrl,
  listBlobKeys,
  deleteBlob,
  getChecklistForTask,
  upsertCloudChecklist,
  deleteCloudChecklist,
} from '../../services/api'
import type { CloudPersona, CloudChecklist, CloudChecklistItem } from '../../services/api'
import { formatDate, toLocalIso, localDatePart, localTimePart, EmptyState, DetailRow, cardStyle, sidebarStyle, detailPanelStyle, listAreaStyle } from './shared'
import { ConfirmDialog, ModalDialog, FormField, SaveButton, DeleteButton, CreateButton, InlineInput, inputStyle, selectStyle } from './CrudHelpers'
import { FullscreenImageViewer } from '../FullscreenImageViewer'
import { AttachmentList, pickAndUploadAttachments, type StoredAttachment } from './AttachmentControls'

/** Task date sections, mirroring the Flutter app's TaskConfig */
const SECTION_OVERDUE = 'Overdue'
const SECTION_TODAY = 'Today'
const SECTION_TOMORROW = 'Tomorrow'
const SECTION_THIS_WEEK = 'Next 7 Days'
const SECTION_LATER = 'Later'
const SECTION_NO_DUE = 'No Due Date'
const SECTION_COMPLETED = 'Completed'

/** Section display order */
const SECTION_ORDER = [
  SECTION_OVERDUE,
  SECTION_TODAY,
  SECTION_TOMORROW,
  SECTION_THIS_WEEK,
  SECTION_LATER,
  SECTION_NO_DUE,
  SECTION_COMPLETED,
] as const

/** Color used as the section header accent */
const SECTION_COLORS: Record<string, string> = {
  [SECTION_OVERDUE]: '#b71c1c',
  [SECTION_TODAY]: '#f44336',
  [SECTION_TOMORROW]: '#F66A4E',
  [SECTION_THIS_WEEK]: '#2196f3',
  [SECTION_LATER]: '#E91E63',
  [SECTION_NO_DUE]: '#9e9e9e',
  [SECTION_COMPLETED]: '#4caf50',
}

/** Status values that count as completed/terminal */
const TERMINAL_STATUSES = new Set([2, 3, 5]) // Completed, Cancelled, Done

/** Determine which date section a task belongs to */
function getTaskSection(task: CloudTask): string {
  const isTerminal = TERMINAL_STATUSES.has(task.status ?? 0)
  const due = task.dueDate ? new Date(task.dueDate) : null
  if (!due || isNaN(due.getTime())) return isTerminal ? SECTION_COMPLETED : SECTION_NO_DUE

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const tomorrow = today + 86400000
  const nextWeek = today + 7 * 86400000
  const taskDay = new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime()

  // Completed/terminal tasks with no or past due dates go to Completed; others stay in their
  // date section (rendered with strikethrough).
  if (isTerminal && taskDay < today) {
    return SECTION_COMPLETED
  }

  if (taskDay < today) return SECTION_OVERDUE
  if (taskDay === today) return SECTION_TODAY
  if (taskDay === tomorrow) return SECTION_TOMORROW
  if (taskDay > today && taskDay < nextWeek) return SECTION_THIS_WEEK
  return SECTION_LATER
}

/** Flutter defaults a missing priority to medium and a missing persona to 'pipar' (Task.fromJson) */
const DEFAULT_PRIORITY = 1
const personaIdOf = (t: CloudTask) => t.personaId || 'pipar'

const TASK_EFFORT_LABELS: Record<number, string> = { 0: 'None', 1: 'Low', 2: 'Medium', 3: 'High' }
const EFFORT_OPTIONS = Object.entries(TASK_EFFORT_LABELS).map(([v, l]) => ({ value: v, label: l }))
const RECURRENCE_LABELS = ['None', 'Hourly', 'Daily', 'Weekly', 'Monthly', 'Yearly']

/**
 * Whether the task has a time of day. In Flutter a task's time lives in
 * scheduledTime (dueDate alone is date-only); events use dueDate unless all-day.
 * A non-midnight dueDate also counts, for tasks created before that convention.
 */
function taskHasTime(t: CloudTask): boolean {
  if (!t.dueDate || t.isAllDay) return false
  if (t.type === 1 || t.scheduledTime) return true
  return localTimePart(t.dueDate) !== '00:00'
}

function formatDue(t: CloudTask): string {
  if (!t.dueDate) return ''
  const d = new Date(t.dueDate)
  if (isNaN(d.getTime())) return t.dueDate
  return taskHasTime(t)
    ? formatDate(t.dueDate)
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/** Sort tasks within a section: by due date/time, then priority, then title */
function sortTasksWithinSection(tasks: CloudTask[]): CloudTask[] {
  return [...tasks].sort((a, b) => {
    const aTime = a.dueDate ? new Date(a.dueDate).getTime() : Number.MAX_SAFE_INTEGER
    const bTime = b.dueDate ? new Date(b.dueDate).getTime() : Number.MAX_SAFE_INTEGER
    if (aTime !== bTime) return aTime - bTime
    const aPrio = a.priority ?? DEFAULT_PRIORITY
    const bPrio = b.priority ?? DEFAULT_PRIORITY
    if (aPrio !== bPrio) return bPrio - aPrio
    return (a.title || '').localeCompare(b.title || '')
  })
}

/** Group tasks by date section */
function groupBySection(tasks: CloudTask[]): Record<string, CloudTask[]> {
  const groups: Record<string, CloudTask[]> = {}
  for (const t of tasks) {
    const section = getTaskSection(t)
    if (!groups[section]) groups[section] = []
    groups[section].push(t)
  }
  for (const key of Object.keys(groups)) {
    groups[key] = sortTasksWithinSection(groups[key])
  }
  return groups
}

const PRIORITY_OPTIONS = Object.entries(TASK_PRIORITY_LABELS).map(([v, l]) => ({ value: v, label: l }))
const STATUS_OPTIONS = Object.entries(TASK_STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }))
const TYPE_OPTIONS = Object.entries(TASK_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }))

/** Sentinel filter value for tasks with no personaId set */
const PERSONA_FILTER_NONE = '__none__'

/** Resolve a personaId to display info: cloud name first, then PERSONA_INFO, then id */
function getPersonaDisplay(
  personaId: string | undefined,
  cloudPersonas: CloudPersona[],
): { name: string; emoji: string } | null {
  if (!personaId) return null
  const cp = cloudPersonas.find((p) => p.id === personaId)
  const info = PERSONA_INFO[personaId]
  return {
    name: cp?.persona_name || info?.name || personaId,
    emoji: info?.emoji || '\u{1F916}',
  }
}

export function TasksView({ focusId }: { focusId?: string } = {}) {
  const [tasks, setTasks] = useState<CloudTask[]>([])
  const [projects, setProjects] = useState<CloudProject[]>([])
  const [cloudPersonas, setCloudPersonas] = useState<CloudPersona[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedTask, setSelectedTask] = useState<CloudTask | null>(null)
  const [editingTask, setEditingTask] = useState<CloudTask | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [filterProjectId, setFilterProjectId] = useState<string | null>(null)
  const [filterPersonaId, setFilterPersonaId] = useState<string | null>(null)
  const [showSidebar, setShowSidebar] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [editingProject, setEditingProject] = useState<CloudProject | null>(null)
  const [uploading, setUploading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [t, p, cp] = await Promise.all([
        listCloudTasks(),
        listCloudProjects(),
        listCloudPersonas().catch(() => [] as CloudPersona[]),
      ])
      setTasks(t)
      setProjects(p)
      setCloudPersonas(cp)
    } catch (e) {
      console.error('Failed to load tasks:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Auto-select the focused task once it's been loaded (deep-link from search).
  useEffect(() => {
    if (!focusId || tasks.length === 0) return
    const match = tasks.find((t) => t.id === focusId)
    if (match && selectedTask?.id !== match.id) {
      setSelectedTask(match)
      setEditingTask({ ...match })
      setIsDirty(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, tasks])

  // When selecting a task, copy it for editing
  const handleSelect = (task: CloudTask) => {
    if (selectedTask?.id === task.id) {
      setSelectedTask(null)
      setEditingTask(null)
      setIsDirty(false)
    } else {
      setSelectedTask(task)
      setEditingTask({ ...task })
      setIsDirty(false)
    }
  }

  const updateFields = (fields: Partial<CloudTask>) => {
    if (!editingTask) return
    setEditingTask({ ...editingTask, ...fields })
    setIsDirty(true)
  }
  const updateField = (field: string, value: unknown) => updateFields({ [field]: value })

  // Mirror Flutter's checkbox handler: completedAt is set on completion and cleared when reopened.
  const updateStatus = (status: number) => {
    if (!editingTask) return
    const completedAt = status === 2
      ? (editingTask.completedAt || new Date().toISOString())
      : undefined
    updateFields({ status, completedAt })
  }

  /** Set due date (YYYY-MM-DD) and optional time (HH:MM); tasks mirror the time into scheduledTime like Flutter. */
  const updateDue = (date: string, time: string) => {
    if (!editingTask) return
    if (!date) {
      updateFields({ dueDate: undefined, scheduledTime: undefined })
      return
    }
    const due = toLocalIso(new Date(`${date}T${time || '00:00'}`))
    const isTask = (editingTask.type ?? 0) === 0
    updateFields({
      dueDate: due,
      scheduledTime: isTask ? (time ? due : undefined) : editingTask.scheduledTime,
    })
  }

  const handleSave = async () => {
    if (!editingTask) return
    setSaving(true)
    try {
      const updated = { ...editingTask, lastUpdatedAt: new Date().toISOString() }
      await upsertCloudTask(updated)
      setTasks((prev) => prev.map((t) => t.id === updated.id ? updated : t))
      setSelectedTask(updated)
      setEditingTask({ ...updated })
      setIsDirty(false)
    } catch (e) {
      console.error('Failed to save task:', e)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedTask) return
    try {
      await deleteCloudTask(selectedTask.id)
      setTasks((prev) => prev.filter((t) => t.id !== selectedTask.id))
      setSelectedTask(null)
      setEditingTask(null)
      setConfirmDelete(false)
    } catch (e) {
      console.error('Failed to delete task:', e)
    }
  }

  // Exclude events — they belong on the calendar, not the task list (matches Flutter's unified_task_list_screen).
  const taskOnly = tasks.filter((t) => (t.type ?? 0) !== 1)

  const projectFilteredTasks = filterProjectId
    ? filterProjectId === '__none__'
      ? taskOnly.filter((t) => !t.projectId)
      : taskOnly.filter((t) => t.projectId === filterProjectId)
    : taskOnly

  const filteredTasks = filterPersonaId
    ? filterPersonaId === PERSONA_FILTER_NONE
      ? projectFilteredTasks.filter((t) => !personaIdOf(t))
      : projectFilteredTasks.filter((t) => personaIdOf(t) === filterPersonaId)
    : projectFilteredTasks

  const grouped = groupBySection(filteredTasks)
  const getProjectName = (id?: string) => projects.find((p) => p.id === id)?.name

  // Personas that own at least one task — keyed by id, ordered by PERSONA_INFO order then alpha
  const personaIdsWithTasks = Array.from(
    new Set(taskOnly.map(personaIdOf)),
  )
  const knownPersonaOrder = Object.keys(PERSONA_INFO)
  personaIdsWithTasks.sort((a, b) => {
    const ai = knownPersonaOrder.indexOf(a)
    const bi = knownPersonaOrder.indexOf(b)
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi)
  })
  const noPersonaCount = taskOnly.filter((t) => !personaIdOf(t)).length

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Project sidebar */}
      {showSidebar && (
        <div style={sidebarStyle}>
          <div style={{ padding: '12px', borderBottom: '1px solid rgba(250,250,250,0.1)' }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'rgba(250,250,250,0.45)', letterSpacing: 0.5, marginBottom: 8 }}>Projects</div>
            <button
              onClick={() => setFilterProjectId(null)}
              style={{ ...projectBtnStyle, background: filterProjectId === null ? 'rgba(45,165,160,0.2)' : 'transparent' }}
            >
              All Tasks ({taskOnly.length})
            </button>
            {projects.map((p) => {
              const count = taskOnly.filter((t) => t.projectId === p.id).length
              return (
                <button
                  key={p.id}
                  onClick={() => setFilterProjectId(p.id)}
                  style={{ ...projectBtnStyle, background: filterProjectId === p.id ? 'rgba(45,165,160,0.2)' : 'transparent' }}
                >
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                  <span style={{ fontSize: 11, color: 'rgba(250,250,250,0.4)' }}>{count}</span>
                </button>
              )
            })}
            {(() => {
              const count = taskOnly.filter((t) => !t.projectId).length
              return count > 0 ? (
                <button
                  onClick={() => setFilterProjectId('__none__')}
                  style={{ ...projectBtnStyle, background: filterProjectId === '__none__' ? 'rgba(45,165,160,0.2)' : 'transparent', fontStyle: 'italic', color: 'rgba(250,250,250,0.5)' }}
                >
                  <span style={{ flex: 1 }}>No Project</span>
                  <span style={{ fontSize: 11, color: 'rgba(250,250,250,0.4)' }}>{count}</span>
                </button>
              ) : null
            })()}
          </div>
          <div style={{ padding: '12px', borderBottom: '1px solid rgba(250,250,250,0.1)' }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'rgba(250,250,250,0.45)', letterSpacing: 0.5, marginBottom: 8 }}>Personas</div>
            <button
              onClick={() => setFilterPersonaId(null)}
              style={{ ...projectBtnStyle, background: filterPersonaId === null ? 'rgba(45,165,160,0.2)' : 'transparent' }}
            >
              All Personas ({taskOnly.length})
            </button>
            {personaIdsWithTasks.map((pid) => {
              const display = getPersonaDisplay(pid, cloudPersonas)
              if (!display) return null
              const count = taskOnly.filter((t) => personaIdOf(t) === pid).length
              return (
                <button
                  key={pid}
                  onClick={() => setFilterPersonaId(pid)}
                  style={{ ...projectBtnStyle, background: filterPersonaId === pid ? 'rgba(45,165,160,0.2)' : 'transparent' }}
                >
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display.name}</span>
                  <span style={{ fontSize: 11, color: 'rgba(250,250,250,0.4)' }}>{count}</span>
                </button>
              )
            })}
            {noPersonaCount > 0 && (
              <button
                onClick={() => setFilterPersonaId(PERSONA_FILTER_NONE)}
                style={{ ...projectBtnStyle, background: filterPersonaId === PERSONA_FILTER_NONE ? 'rgba(45,165,160,0.2)' : 'transparent', fontStyle: 'italic', color: 'rgba(250,250,250,0.5)' }}
              >
                <span style={{ flex: 1 }}>No Persona</span>
                <span style={{ fontSize: 11, color: 'rgba(250,250,250,0.4)' }}>{noPersonaCount}</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Task list */}
      <div style={listAreaStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid rgba(250,250,250,0.1)' }}>
          <button onClick={() => setShowSidebar(!showSidebar)} style={toggleBtnStyle}>
            {showSidebar ? '\u{25C0}' : '\u{25B6}'} Projects
          </button>
          <span style={{ flex: 1 }} />
          <CreateButton label="New Task" onClick={() => setShowCreate(true)} />
          <button onClick={load} style={toggleBtnStyle} disabled={loading}>
            {loading ? '...' : 'Refresh'}
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '8px 16px' }}>
          {/* Project header — only shown when a real project is selected.
              Mirrors Flutter's ProjectViewScreen banner with name, optional
              description and an Edit button that opens the edit modal. */}
          {(() => {
            if (!filterProjectId || filterProjectId === '__none__') return null
            const proj = projects.find((p) => p.id === filterProjectId)
            if (!proj) return null
            const total = filteredTasks.length
            const done = filteredTasks.filter((t) => TERMINAL_STATUSES.has(t.status ?? 0)).length
            return (
              <div style={{ marginBottom: 16, padding: '14px 16px', background: 'rgba(45,165,160,0.12)', border: '1px solid rgba(45,165,160,0.3)', borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ flex: 1, fontSize: 18, fontWeight: 600 }}>{proj.name || 'Untitled project'}</span>
                  <span style={{ fontSize: 11, color: 'rgba(250,250,250,0.5)' }}>{done}/{total} done</span>
                  <button
                    onClick={() => setEditingProject(proj)}
                    style={{ ...toggleBtnStyle, borderColor: 'rgba(45,165,160,0.4)', color: '#2DA5A0' }}
                  >
                    Edit
                  </button>
                </div>
                {proj.description && (
                  <div style={{ marginTop: 8, fontSize: 13, color: 'rgba(250,250,250,0.7)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                    {proj.description}
                  </div>
                )}
              </div>
            )
          })()}

          {loading && <div style={{ padding: 24, textAlign: 'center', color: 'rgba(250,250,250,0.4)' }}>Loading...</div>}
          {!loading && filteredTasks.length === 0 && <EmptyState label="No tasks found" />}
          {!loading && SECTION_ORDER.map((section) => {
            const group = grouped[section]
            if (!group || group.length === 0) return null
            const accent = SECTION_COLORS[section]
            return (
              <div key={section} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: accent, display: 'inline-block' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: accent, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {section} ({group.length})
                  </span>
                </div>
                {group.map((task) => {
                  const persona = getPersonaDisplay(personaIdOf(task), cloudPersonas)
                  const isDone = TERMINAL_STATUSES.has(task.status ?? 0)
                  return (
                    <div
                      key={task.id}
                      onClick={() => handleSelect(task)}
                      style={{
                        ...cardStyle,
                        cursor: 'pointer',
                        borderLeft: `3px solid ${TASK_PRIORITY_COLORS[task.priority ?? DEFAULT_PRIORITY] || '#555'}`,
                        background: selectedTask?.id === task.id ? 'rgba(45,165,160,0.15)' : cardStyle.background,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {task.emoji && <span>{task.emoji}</span>}
                        <span
                          style={{
                            flex: 1,
                            fontWeight: 500,
                            textDecoration: isDone ? 'line-through' : 'none',
                            opacity: isDone ? 0.6 : 1,
                          }}
                        >
                          {task.title || 'Untitled'}
                        </span>
                        {persona && (
                          <span
                            title={`Owned by ${persona.name}`}
                            style={{
                              fontSize: 11,
                              padding: '1px 6px',
                              borderRadius: 10,
                              background: 'rgba(250,250,250,0.08)',
                              color: 'rgba(250,250,250,0.75)',
                            }}
                          >
                            {persona.name}
                          </span>
                        )}
                        {task.type === 1 && <span style={badgeStyle('#1565c0')}>Event</span>}
                        {(task.priority ?? DEFAULT_PRIORITY) > 0 && (
                          <span style={badgeStyle(TASK_PRIORITY_COLORS[task.priority ?? DEFAULT_PRIORITY] || '#666')}>
                            {TASK_PRIORITY_LABELS[task.priority ?? DEFAULT_PRIORITY]}
                          </span>
                        )}
                      </div>
                      {task.dueDate && (
                        <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.5)', marginTop: 4 }}>
                          Due: {formatDue(task)}
                        </div>
                      )}
                      {task.projectId && (
                        <div style={{ fontSize: 11, color: '#4fc3f7', marginTop: 2 }}>
                          {getProjectName(task.projectId) || 'Unknown project'}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>

      {/* Editable detail panel */}
      {editingTask && (
        <div style={detailPanelStyle}>
          <div style={{ padding: 16, borderBottom: '1px solid rgba(250,250,250,0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: 'rgba(250,250,250,0.5)' }}>Edit Task</span>
            <SaveButton dirty={isDirty} saving={saving} onClick={handleSave} />
            <DeleteButton onClick={() => setConfirmDelete(true)} />
            <button onClick={() => { setSelectedTask(null); setEditingTask(null); setIsDirty(false) }} style={toggleBtnStyle}>X</button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Title</label>
              <InlineInput
                value={editingTask.title || ''}
                onChange={(v) => updateField('title', v)}
                placeholder="Task title"
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Description</label>
              <InlineInput
                type="textarea"
                value={editingTask.description || ''}
                onChange={(v) => updateField('description', v)}
                placeholder="Description"
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Status</label>
              <select
                style={selectStyle}
                value={String(editingTask.status ?? 0)}
                onChange={(e) => updateStatus(Number((e.target as HTMLSelectElement).value))}
              >
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Priority</label>
              <select
                style={selectStyle}
                value={String(editingTask.priority ?? DEFAULT_PRIORITY)}
                onChange={(e) => updateField('priority', Number((e.target as HTMLSelectElement).value))}
              >
                {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {(editingTask.type ?? 0) === 0 && (
              <div style={{ marginBottom: 12 }}>
                <label style={fieldLabelStyle}>Effort</label>
                <select
                  style={selectStyle}
                  value={String(editingTask.effort ?? 2)}
                  onChange={(e) => updateField('effort', Number((e.target as HTMLSelectElement).value))}
                >
                  {EFFORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Persona</label>
              <select
                style={selectStyle}
                value={personaIdOf(editingTask)}
                onChange={(e) => updateField('personaId', (e.target as HTMLSelectElement).value)}
              >
                {Array.from(new Set([
                  personaIdOf(editingTask),
                  ...cloudPersonas.map((p) => p.id),
                  ...Object.keys(PERSONA_INFO).filter((id) => id !== 'custom'),
                ])).map((id) => (
                  <option key={id} value={id}>{getPersonaDisplay(id, cloudPersonas)?.name ?? id}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Type</label>
              <select
                style={selectStyle}
                value={String(editingTask.type ?? 0)}
                onChange={(e) => updateField('type', Number((e.target as HTMLSelectElement).value))}
              >
                {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={fieldLabelStyle}>Due Date</label>
                <input
                  type="date"
                  style={inputStyle}
                  value={localDatePart(editingTask.dueDate)}
                  onInput={(e) => {
                    const date = (e.target as HTMLInputElement).value
                    updateDue(date, taskHasTime(editingTask) ? localTimePart(editingTask.dueDate) : '')
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={fieldLabelStyle}>Due Time</label>
                <input
                  type="time"
                  style={inputStyle}
                  disabled={!!editingTask.isAllDay}
                  value={taskHasTime(editingTask) ? localTimePart(editingTask.dueDate) : ''}
                  onInput={(e) => {
                    const time = (e.target as HTMLInputElement).value
                    updateDue(localDatePart(editingTask.dueDate) || localDatePart(new Date().toISOString()), time)
                  }}
                />
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, fontSize: 13, color: 'rgba(250,250,250,0.7)' }}>
              <input
                type="checkbox"
                checked={!!editingTask.isAllDay}
                disabled={!editingTask.dueDate}
                onChange={(e) => updateField('isAllDay', (e.target as HTMLInputElement).checked)}
              />
              All day
            </label>
            {(() => {
              const freq = Number((editingTask.recurrence as { frequency?: number } | undefined)?.frequency ?? 0)
              if (!freq) return null
              const interval = Number((editingTask.recurrence as { interval?: number }).interval ?? 1)
              return (
                <div style={{ marginBottom: 12 }}>
                  <DetailRow
                    label="Repeats"
                    value={`${RECURRENCE_LABELS[freq] ?? 'Custom'}${interval > 1 ? ` (every ${interval})` : ''} — edit on mobile`}
                  />
                </div>
              )
            })()}
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Location</label>
              <InlineInput
                value={editingTask.location || ''}
                onChange={(v) => updateField('location', v || undefined)}
                placeholder="Location"
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Project</label>
              <select
                style={selectStyle}
                value={editingTask.projectId || ''}
                onChange={(e) => updateField('projectId', (e.target as HTMLSelectElement).value || undefined)}
              >
                <option value="">No project</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Tags</label>
              <TagsInput
                key={editingTask.id}
                tags={editingTask.tags || []}
                onChange={(tags) => updateField('tags', tags)}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Emoji</label>
              <InlineInput
                value={editingTask.emoji || ''}
                onChange={(v) => updateField('emoji', v || undefined)}
                placeholder="e.g. \u{1F4DD}"
                style={{ width: 60 }}
              />
            </div>

            {/* Subtasks — backed by cloud_checklists. Mobile (and the
                AI tools) store subtasks in a separate `checklists` table,
                NOT on `task.subTasks`. Pulling them here means the same
                list the user sees on mobile shows up on the web client.
                The TaskChecklist component handles fetch + add + toggle
                + remove against the /cloud_data/checklists endpoint. */}
            <div style={{ marginTop: 12 }}>
              <TaskChecklist taskId={editingTask.id} />
            </div>

            {/* Image attachments — Flutter writes parallel arrays of paths
                and R2 blob keys; web client only has access to the cloud
                blobs, so any local-only images (empty blob key) are skipped.
                The X button on each thumbnail removes the entry from both
                parallel arrays, persists the task, and deletes the R2 blob
                so storage doesn't accumulate orphans. */}
            <div style={{ marginTop: 12 }}>
              <label style={fieldLabelStyle}>Images</label>
              <TaskImages
                task={editingTask}
                onRemoveBlob={async (blobKey) => {
                  // Find the matching index in imageBlobKeys; drop both
                  // parallel arrays at that position. If the blob came
                  // from the listBlobKeys fallback (legacy data not in the
                  // arrays), there's nothing to filter — but we still
                  // delete from R2 so the next listBlobKeys read won't
                  // resurrect it.
                  const blobKeys = (Array.isArray(editingTask.imageBlobKeys) ? editingTask.imageBlobKeys : []) as string[]
                  const paths = (Array.isArray(editingTask.imagePaths) ? editingTask.imagePaths : []) as string[]
                  const idx = blobKeys.indexOf(blobKey)
                  let nextBlobKeys = blobKeys
                  let nextPaths = paths
                  if (idx >= 0) {
                    nextBlobKeys = [...blobKeys.slice(0, idx), ...blobKeys.slice(idx + 1)]
                    nextPaths = idx < paths.length
                      ? [...paths.slice(0, idx), ...paths.slice(idx + 1)]
                      : paths
                  }
                  const updated: CloudTask = {
                    ...editingTask,
                    imagePaths: nextPaths,
                    imageBlobKeys: nextBlobKeys,
                    lastUpdatedAt: new Date().toISOString(),
                  }
                  await upsertCloudTask(updated)
                  setTasks((prev) => prev.map((t) => t.id === updated.id ? updated : t))
                  setSelectedTask(updated)
                  setEditingTask({ ...updated })
                  setIsDirty(false)
                  // Best-effort R2 cleanup. Silent failure leaves an orphan
                  // blob; the metadata removal is what matters for the user.
                  void deleteBlob(blobKey)
                }}
              />
            </div>

            {/* Image attachments. Picks are routed to the parallel
                `imagePaths` / `imageBlobKeys` arrays Flutter's Task model
                reads, so they appear in the "Images (from mobile)" panel
                above and on mobile after sync. Document attachments on
                tasks were removed — mobile's Task model has no slot for
                them, and the embedding pipeline only covers note-attached
                files anyway. */}
            <div style={{ marginTop: 8 }}>
              <AttachmentList
                attachments={(editingTask.attachments as unknown as StoredAttachment[]) || []}
                uploading={uploading}
                onChange={(next) => updateField('attachments', next)}
                onAddImages={async () => {
                  setUploading(true)
                  try {
                    const added = await pickAndUploadAttachments('tasks', editingTask.id, true, false)
                    if (added.length === 0) return
                    const split = splitForTaskSync(
                      added,
                      editingTask.imagePaths,
                      editingTask.imageBlobKeys,
                    )
                    // Anything that wasn't tagged as an image (rare from the
                    // image-only picker; keeps us safe) folds into the
                    // web-only attachments list.
                    const nextAttachments = split.remainder.length > 0
                      ? [...((editingTask.attachments as unknown as StoredAttachment[]) || []), ...split.remainder]
                      : (editingTask.attachments as unknown as StoredAttachment[]) || undefined
                    // Persist the task immediately. Without this, the
                    // imageBlobKeys would only live in local state until the
                    // user clicked Save — and a sync-down to mobile in the
                    // meantime would never see the new image. The blob is
                    // already in R2 from the upload above, so this is a
                    // tiny incremental DB write.
                    const updated: CloudTask = {
                      ...editingTask,
                      imagePaths: split.imagePaths,
                      imageBlobKeys: split.imageBlobKeys,
                      lastUpdatedAt: new Date().toISOString(),
                    }
                    if (nextAttachments !== undefined) {
                      ;(updated as Record<string, unknown>).attachments = nextAttachments
                    }
                    await upsertCloudTask(updated)
                    setTasks((prev) => prev.map((t) => t.id === updated.id ? updated : t))
                    setSelectedTask(updated)
                    setEditingTask({ ...updated })
                    // The task (including any prior dirty edits to other
                    // fields) is now persisted, so reset the dirty flag.
                    setIsDirty(false)
                  } finally {
                    setUploading(false)
                  }
                }}
              />
            </div>

            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(250,250,250,0.1)' }}>
              <DetailRow label="Created" value={formatDate(editingTask.createdAt)} />
              <DetailRow label="Updated" value={formatDate(editingTask.lastUpdatedAt)} />
              {editingTask.completedAt && <DetailRow label="Completed" value={formatDate(editingTask.completedAt)} />}
            </div>
          </div>
        </div>
      )}

      {/* Create task modal */}
      {showCreate && (
        <CreateTaskModal
          projects={projects}
          onClose={() => setShowCreate(false)}
          onCreate={async (task) => {
            await upsertCloudTask(task)
            setTasks((prev) => [task, ...prev])
            setShowCreate(false)
          }}
        />
      )}

      {/* Delete confirm */}
      {confirmDelete && selectedTask && (
        <ConfirmDialog
          message={`Delete task "${selectedTask.title || 'Untitled'}"?`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}

      {/* Edit project modal */}
      {editingProject && (
        <EditProjectModal
          project={editingProject}
          onClose={() => setEditingProject(null)}
          onSave={async (updated) => {
            await upsertCloudProject(updated)
            setProjects((prev) => prev.map((p) => p.id === updated.id ? updated : p))
            setEditingProject(null)
          }}
        />
      )}
    </div>
  )
}

// ── Create Task Modal ─────────────────────────────────────────────

function CreateTaskModal({ projects, onClose, onCreate }: {
  projects: CloudProject[]
  onClose: () => void
  onCreate: (task: CloudTask) => Promise<void>
}) {
  // Pre-generate the task id so attachment uploads can hit R2 immediately
  // (entity_type=tasks, entity_id=this id) and end up linked when the task
  // is saved. Same trade-off as CreateNoteModal: cancel-after-upload leaves
  // an orphan blob, which is the existing behaviour for the edit flow.
  const [taskId] = useState(() => crypto.randomUUID())
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('1')
  const [status, setStatus] = useState('0')
  const [type, setType] = useState('0')
  const [dueDate, setDueDate] = useState('')
  const [dueTime, setDueTime] = useState('')
  const [location, setLocation] = useState('')
  const [projectId, setProjectId] = useState('')
  const [tags, setTags] = useState('')
  const [creating, setCreating] = useState(false)
  const [attachments, setAttachments] = useState<StoredAttachment[]>([])
  // Parallel arrays for image attachments — these flow into the Flutter
  // Task model fields mobile reads, so web-uploaded images appear on
  // mobile after sync. Non-image attachments stay in `attachments`.
  const [imagePaths, setImagePaths] = useState<string[]>([])
  const [imageBlobKeys, setImageBlobKeys] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)

  const handleCreate = async () => {
    if (!title.trim()) return
    setCreating(true)
    try {
      const now = new Date().toISOString()
      const due = dueDate ? toLocalIso(new Date(`${dueDate}T${dueTime || '00:00'}`)) : undefined
      const task: CloudTask = {
        id: taskId,
        title: title.trim(),
        description: description.trim() || undefined,
        priority: Number(priority),
        status: Number(status),
        type: Number(type),
        dueDate: due,
        scheduledTime: due && dueTime && type === '0' ? due : undefined,
        completedAt: status === '2' ? now : undefined,
        personaId: 'pipar',
        location: location.trim() || undefined,
        projectId: projectId || undefined,
        tags: tags ? tags.split(',').map((t) => t.trim()).filter(Boolean) : undefined,
        createdAt: now,
        lastUpdatedAt: now,
      }
      if (attachments.length > 0) {
        // CloudTask is an open record — the web stores web-only attachments
        // (PDFs, MD, HTML) here. Image attachments go through imagePaths /
        // imageBlobKeys below so mobile's Task model picks them up.
        ;(task as Record<string, unknown>).attachments = attachments
      }
      if (imageBlobKeys.length > 0) {
        task.imagePaths = imagePaths
        task.imageBlobKeys = imageBlobKeys
      }
      await onCreate(task)
    } catch (e) {
      console.error('Failed to create task:', e)
    } finally {
      setCreating(false)
    }
  }

  const handlePickImages = async () => {
    setUploading(true)
    try {
      const added = await pickAndUploadAttachments('tasks', taskId, true, false)
      if (added.length > 0) {
        // Route images into the parallel arrays mobile reads. Anything that
        // somehow comes back as non-image (defensive) falls back to the
        // web-only attachments list.
        const split = splitForTaskSync(added, imagePaths, imageBlobKeys)
        setImagePaths(split.imagePaths)
        setImageBlobKeys(split.imageBlobKeys)
        if (split.remainder.length > 0) {
          setAttachments((prev) => [...prev, ...split.remainder])
        }
      }
    } finally {
      setUploading(false)
    }
  }

  // Document attachments on tasks were removed — Flutter's Task model has no
  // slot for them, so anything uploaded here would have lived web-only and
  // bypassed search_memory's embedding pipeline. Image attachments are
  // still supported via the parallel imagePaths/imageBlobKeys arrays.

  return (
    <ModalDialog
      title="New Task"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: 'rgba(250,250,250,0.1)', color: 'rgba(250,250,250,0.7)', cursor: 'pointer', fontSize: 13 }}>
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!title.trim() || creating || uploading}
            style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: '#2DA5A0', color: '#fff', cursor: 'pointer', fontSize: 13, opacity: !title.trim() || creating || uploading ? 0.5 : 1 }}
          >
            {creating ? 'Creating...' : 'Create'}
          </button>
        </>
      }
    >
      <FormField label="Title" value={title} onChange={setTitle} required placeholder="Task title" />
      <FormField label="Description" value={description} onChange={setDescription} type="textarea" placeholder="Description" />
      <FormField label="Type" value={type} onChange={setType} type="select" options={TYPE_OPTIONS} />
      <FormField label="Priority" value={priority} onChange={setPriority} type="select" options={PRIORITY_OPTIONS} />
      <FormField label="Status" value={status} onChange={setStatus} type="select" options={STATUS_OPTIONS} />
      <FormField label="Due Date" value={dueDate} onChange={setDueDate} type="date" />
      <FormField label="Due Time" value={dueTime} onChange={setDueTime} type="time" />
      <FormField label="Location" value={location} onChange={setLocation} placeholder="Location" />
      <FormField label="Project" value={projectId} onChange={setProjectId} type="select" options={[{ value: '', label: 'No project' }, ...projects.map((p) => ({ value: p.id, label: p.name }))]} />
      <FormField label="Tags" value={tags} onChange={setTags} placeholder="tag1, tag2, ..." />
      <div style={{ marginTop: 12 }}>
        <label style={{ ...fieldLabelStyle, marginBottom: 6 }}>Images</label>
        <AttachmentList
          attachments={attachments}
          uploading={uploading}
          onChange={setAttachments}
          onAddImages={handlePickImages}
        />
      </div>
    </ModalDialog>
  )
}

// ── Tags input ────────────────────────────────────────────────────

/**
 * Comma-separated tag editor. Keeps the raw text locally so typing a comma
 * isn't immediately normalized away by re-rendering from the parsed array.
 */
function TagsInput({ tags, onChange }: { tags: string[]; onChange: (tags: string[]) => void }) {
  const [text, setText] = useState(tags.join(', '))
  return (
    <InlineInput
      value={text}
      onChange={(v) => {
        setText(v)
        onChange(v.split(',').map((t) => t.trim()).filter(Boolean))
      }}
      placeholder="tag1, tag2, ..."
    />
  )
}

// ── Task Checklist (cloud_checklists) ──────────────────────────────

/**
 * Renders the subtasks for a task. Subtasks live in a separate
 * `cloud_checklists` table — one Checklist row per Task, each row
 * carrying an items[] list. Mobile's `ChecklistProvider` is the canonical
 * writer; this component lets the web user read + toggle + add + remove
 * against the same data set so subtasks stay in sync across devices.
 */
function TaskChecklist({ taskId }: { taskId: string }) {
  const [checklist, setChecklist] = useState<CloudChecklist | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setChecklist(await getChecklistForTask(taskId))
    } finally {
      setLoading(false)
    }
  }, [taskId])

  useEffect(() => { load() }, [load])

  const persist = async (next: CloudChecklist) => {
    setBusy(true)
    try {
      await upsertCloudChecklist(next)
      setChecklist(next)
    } finally {
      setBusy(false)
    }
  }

  const handleAdd = async (title: string) => {
    const now = new Date().toISOString()
    const newItem: CloudChecklistItem = {
      id: crypto.randomUUID(),
      title,
      isCompleted: false,
      createdAt: now,
      sortOrder: (checklist?.items.length ?? 0),
    }
    if (checklist) {
      await persist({
        ...checklist,
        items: [...checklist.items, newItem],
        lastUpdatedAt: now,
      })
    } else {
      // First subtask on this task — bootstrap a fresh Checklist row.
      await persist({
        id: crypto.randomUUID(),
        title: 'Subtasks',
        taskId,
        items: [newItem],
        createdAt: now,
        lastUpdatedAt: now,
      })
    }
  }

  const handleToggle = async (itemId: string) => {
    if (!checklist) return
    const now = new Date().toISOString()
    await persist({
      ...checklist,
      items: checklist.items.map((it) => it.id === itemId
        ? { ...it, isCompleted: !it.isCompleted, completedAt: !it.isCompleted ? now : undefined }
        : it),
      lastUpdatedAt: now,
    })
  }

  const handleRemove = async (itemId: string) => {
    if (!checklist) return
    const nextItems = checklist.items.filter((it) => it.id !== itemId)
    if (nextItems.length === 0) {
      // Whole checklist is now empty — drop it entirely so it doesn't
      // linger as a zero-item row on cloud (matches mobile's behaviour
      // where ChecklistProvider.removeItem keeps the checklist alive,
      // but the web UX is cleaner without an empty header).
      setBusy(true)
      try {
        await deleteCloudChecklist(checklist.id)
        setChecklist(null)
      } finally {
        setBusy(false)
      }
      return
    }
    await persist({
      ...checklist,
      items: nextItems,
      lastUpdatedAt: new Date().toISOString(),
    })
  }

  const items = checklist?.items || []
  const done = items.filter((i) => i.isCompleted).length
  const label = items.length > 0 ? `Subtasks (${done}/${items.length})` : 'Subtasks'

  return (
    <div style={{ opacity: busy ? 0.6 : 1 }}>
      <label style={fieldLabelStyle}>{label}</label>
      {loading && <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.4)' }}>Loading…</div>}
      {!loading && items.map((st) => (
        <div key={st.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <input
            type="checkbox"
            checked={st.isCompleted}
            disabled={busy}
            onChange={() => handleToggle(st.id)}
          />
          <span style={{ fontSize: 13, color: st.isCompleted ? '#4caf50' : 'rgba(250,250,250,0.7)', flex: 1, textDecoration: st.isCompleted ? 'line-through' : 'none' }}>
            {st.title}
          </span>
          <button
            onClick={() => handleRemove(st.id)}
            disabled={busy}
            style={{ background: 'none', border: 'none', color: 'rgba(250,250,250,0.3)', cursor: 'pointer', fontSize: 14 }}
            title="Remove subtask"
          >
            x
          </button>
        </div>
      ))}
      <AddSubtaskInput onAdd={handleAdd} />
    </div>
  )
}

// ── Add Subtask Input ──────────────────────────────────────────────

function AddSubtaskInput({ onAdd }: { onAdd: (title: string) => void }) {
  const [text, setText] = useState('')
  const submit = () => {
    const t = text.trim()
    if (!t) return
    onAdd(t)
    setText('')
  }
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
      <input
        type="text"
        value={text}
        onInput={(e) => setText((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit() } }}
        placeholder="Add subtask..."
        style={{ ...inputStyle, flex: 1, padding: '6px 8px', fontSize: 12 }}
      />
      <button
        onClick={submit}
        disabled={!text.trim()}
        style={{
          padding: '6px 12px',
          border: '1px solid rgba(45,165,160,0.4)',
          borderRadius: 6,
          background: text.trim() ? 'rgba(45,165,160,0.15)' : 'transparent',
          color: text.trim() ? '#2DA5A0' : 'rgba(250,250,250,0.3)',
          cursor: text.trim() ? 'pointer' : 'default',
          fontSize: 12,
        }}
      >
        Add
      </button>
    </div>
  )
}

// ── Task Images ────────────────────────────────────────────────────

/**
 * AttachmentType.image index, matching mobile's `note_model.dart` enum.
 * Webclient's `pickAndUploadAttachments` populates `type` from this enum
 * via `attachmentTypeFor()`. Image attachments need to ride the parallel
 * `imagePaths` / `imageBlobKeys` arrays so mobile's Task model reads them
 * — non-image attachments stay in the web-only `attachments` field.
 */
const ATT_TYPE_IMAGE = 0

/**
 * Split freshly-uploaded attachments into image-typed (mobile-syncable via
 * the parallel-array convention) and non-image (web-only). Returns the new
 * pair of {imagePaths, imageBlobKeys} arrays plus the leftover non-image
 * attachments. `imagePaths` for cloud-only entries is intentionally an
 * empty string — mobile's `_downloadTaskImages` treats empty paths as
 * "fetch from blob key" and writes the local cache path on download.
 */
function splitForTaskSync(
  added: StoredAttachment[],
  existingImagePaths: string[] | undefined,
  existingImageBlobKeys: string[] | undefined,
): {
  imagePaths: string[]
  imageBlobKeys: string[]
  remainder: StoredAttachment[]
} {
  const imagePaths = Array.isArray(existingImagePaths) ? [...existingImagePaths] : []
  const imageBlobKeys = Array.isArray(existingImageBlobKeys) ? [...existingImageBlobKeys] : []
  // Backfill blobKeys length to match imagePaths so the parallel-array
  // invariant Flutter relies on holds even if the existing data was wonky.
  while (imageBlobKeys.length < imagePaths.length) imageBlobKeys.push('')
  while (imagePaths.length < imageBlobKeys.length) imagePaths.push('')

  const remainder: StoredAttachment[] = []
  for (const att of added) {
    if (att.type === ATT_TYPE_IMAGE && att.blobKey) {
      imagePaths.push('') // No local fs path on web; mobile resolves from blobKey
      imageBlobKeys.push(att.blobKey)
    } else {
      remainder.push(att)
    }
  }
  return { imagePaths, imageBlobKeys, remainder }
}

/**
 * Resolve cloud blob keys for a task. Prefers the camelCase imageBlobKeys
 * array Flutter writes (skipping empty placeholders for not-yet-uploaded
 * entries), and falls back to listing all blobs registered for the task.
 */
async function resolveTaskBlobKeys(task: CloudTask): Promise<string[]> {
  const fromArray = (Array.isArray(task.imageBlobKeys) ? task.imageBlobKeys : [])
    .filter((k): k is string => typeof k === 'string' && k.length > 0)
  if (fromArray.length > 0) return fromArray
  try {
    return await listBlobKeys('tasks', task.id)
  } catch {
    return []
  }
}

function TaskImages({ task, onRemoveBlob }: { task: CloudTask; onRemoveBlob?: (blobKey: string) => Promise<void> | void }) {
  // Track URL ↔ blobKey alignment so the per-thumbnail delete handler
  // knows which key to remove from the parallel arrays. Indices match.
  const [items, setItems] = useState<Array<{ url: string; blobKey: string }>>([])
  const [loading, setLoading] = useState(true)
  const [fullscreen, setFullscreen] = useState<string | null>(null)
  const [removing, setRemoving] = useState<string | null>(null)
  const localOnlyCount =
    (Array.isArray(task.imagePaths) ? task.imagePaths.length : 0) -
    (Array.isArray(task.imageBlobKeys) ? task.imageBlobKeys.filter((k) => k).length : 0)

  // Re-run when the set of blob keys changes — not just when the task id
  // does. Without `imageBlobKeysSig` in the deps, attaching a new image
  // wouldn't refresh the thumbnails until the dialog was closed and
  // reopened (a fresh mount of this component). Stringified to keep deps
  // primitive so the effect doesn't re-fire on identity-equal arrays.
  const imageBlobKeysSig = (Array.isArray(task.imageBlobKeys) ? task.imageBlobKeys : []).join('|')
  useEffect(() => {
    let cancelled = false
    setItems([])
    setLoading(true)
    setFullscreen(null)
    ;(async () => {
      const keys = await resolveTaskBlobKeys(task)
      if (cancelled) return
      const results = await Promise.all(keys.map((k) => downloadBlobAsUrl(k).catch(() => null)))
      if (cancelled) return
      const next: Array<{ url: string; blobKey: string }> = []
      results.forEach((r, i) => {
        if (r && r.mimeType.startsWith('image/')) {
          next.push({ url: r.url, blobKey: keys[i] })
        }
      })
      setItems(next)
      setLoading(false)
    })().catch(() => { if (!cancelled) setLoading(false) })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id, imageBlobKeysSig])

  const handleRemove = async (blobKey: string) => {
    if (!onRemoveBlob || !blobKey) return
    if (!confirm('Remove this image from the task?')) return
    setRemoving(blobKey)
    try {
      await onRemoveBlob(blobKey)
    } finally {
      setRemoving(null)
    }
  }

  if (loading) {
    return <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.4)' }}>Loading images...</div>
  }
  if (items.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.4)' }}>
        {localOnlyCount > 0
          ? `${localOnlyCount} image(s) attached on mobile only — not synced to cloud`
          : 'No images attached'}
      </div>
    )
  }
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 6 }}>
        {items.map((it, i) => {
          const isRemoving = removing === it.blobKey
          return (
            <div key={`${it.blobKey}-${i}`} style={{ position: 'relative' }}>
              <img
                src={it.url}
                alt={`attachment ${i + 1}`}
                onClick={() => setFullscreen(it.url)}
                title="Click to view fullscreen"
                style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in', background: 'rgba(0,0,0,0.3)', opacity: isRemoving ? 0.4 : 1 }}
              />
              {onRemoveBlob && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemove(it.blobKey)
                  }}
                  disabled={isRemoving}
                  title="Remove image"
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    border: 'none',
                    background: 'rgba(0,0,0,0.6)',
                    color: 'rgba(255,255,255,0.95)',
                    cursor: isRemoving ? 'default' : 'pointer',
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                  }}
                >
                  {isRemoving ? '\u{2026}' : '\u{00D7}'}
                </button>
              )}
            </div>
          )
        })}
      </div>
      {localOnlyCount > 0 && (
        <div style={{ fontSize: 11, color: 'rgba(250,250,250,0.35)', marginTop: 4 }}>
          + {localOnlyCount} image(s) on mobile only
        </div>
      )}
      {fullscreen && (
        <FullscreenImageViewer src={fullscreen} alt={task.title} onClose={() => setFullscreen(null)} />
      )}
    </div>
  )
}

// ── Edit Project Modal ─────────────────────────────────────────────

function EditProjectModal({ project, onClose, onSave }: {
  project: CloudProject
  onClose: () => void
  onSave: (project: CloudProject) => Promise<void>
}) {
  const [name, setName] = useState(project.name || '')
  const [description, setDescription] = useState(project.description || '')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!name.trim()) return
    setSaving(true)
    try {
      const updated: CloudProject = {
        ...project,
        name: name.trim(),
        description: description.trim(),
        updated_at: new Date().toISOString(),
      }
      await onSave(updated)
    } catch (e) {
      console.error('Failed to save project:', e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <ModalDialog
      title="Edit Project"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: 'rgba(250,250,250,0.1)', color: 'rgba(250,250,250,0.7)', cursor: 'pointer', fontSize: 13 }}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim() || saving}
            style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: '#2DA5A0', color: '#fff', cursor: 'pointer', fontSize: 13, opacity: !name.trim() || saving ? 0.5 : 1 }}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </>
      }
    >
      <FormField label="Name" value={name} onChange={setName} required placeholder="Project name" />
      <FormField label="Description" value={description} onChange={setDescription} type="textarea" placeholder="Description" />
    </ModalDialog>
  )
}

// ── Styles ─────────────────────────────────────────────────────────

const projectBtnStyle: Record<string, string | number> = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '8px 10px',
  border: 'none',
  borderRadius: 6,
  color: '#FAFAFA',
  cursor: 'pointer',
  fontSize: 13,
  textAlign: 'left',
  marginBottom: 2,
}

const toggleBtnStyle: Record<string, string | number> = {
  padding: '4px 10px',
  border: '1px solid rgba(250,250,250,0.15)',
  borderRadius: 6,
  background: 'transparent',
  color: 'rgba(250,250,250,0.6)',
  cursor: 'pointer',
  fontSize: 12,
}

const badgeStyle = (bg: string): Record<string, string | number> => ({
  fontSize: 10,
  padding: '1px 6px',
  borderRadius: 3,
  background: bg,
  color: '#fff',
})

const fieldLabelStyle: Record<string, string | number> = {
  fontSize: 12,
  color: 'rgba(250,250,250,0.5)',
  marginBottom: 4,
  display: 'block',
}
